/* eslint-disable @typescript-eslint/no-explicit-any */

import axios, { AxiosError, AxiosRequestConfig, InternalAxiosRequestConfig } from 'axios';

import { AUTH_URLS } from '@/api/url';
import useAdminAuth from '@/store/adminAuth';
import useAuth from '@/store/auth';

export interface ApiResponse<T> {
  code: number;
  message: string;
  data: T;
}

const baseURL: string = import.meta.env.VITE_API_BASE_URL;

const createApiInstance = (authHook: any) => {
  const instance = axios.create({
    baseURL,
    timeout: 300000,
    headers: {
      'Content-Type': 'application/json'
    }
  });

  instance.interceptors.request.use(
    async (config): Promise<InternalAxiosRequestConfig> => {
      const { accessToken } = authHook.getState();

      if (!config.headers.Authorization && accessToken) {
        config.headers.Authorization = `Bearer ${accessToken}`;
      }

      return config;
    },
    (error) => {
      console.error('Request error:', error);
      return Promise.reject(error);
    }
  );

  instance.interceptors.response.use(
    (response) => response,
    async (error: AxiosError) => {
      console.error('Response error:', error);

      const { accessToken, refreshToken, login, logout, user } = authHook.getState();

      if (error.response?.status === 401) {
        if (!accessToken || !refreshToken) {
          logout();
          return Promise.reject(error);
        }

        try {
          const res = await fetch(AUTH_URLS.REFRESH_TOKEN, {
            method: 'POST',
            headers: {
              authorization: `Bearer ${refreshToken}`
            }
          });

          const { data } = await res.json();

          if (!data) {
            logout();
            return Promise.reject(error);
          }

          const { accessToken: newAccessToken, refreshToken: newRefreshToken } = data;

          login(user, newAccessToken, newRefreshToken);

          if (error.config?.headers) {
            error.config.headers.Authorization = `Bearer ${newAccessToken}`;
          }

          return axios.request(error.config as AxiosRequestConfig);
        } catch (refreshError) {
          console.error('Token refresh error:', refreshError);
          logout();
          return Promise.reject(refreshError);
        }
      }

      return Promise.reject(error);
    }
  );

  return instance;
};

export const api = createApiInstance(useAuth);
export const apiAdmin = createApiInstance(useAdminAuth);

const visitorToken: string = import.meta.env.VITE_API_VISITOR_TOKEN;

export const apiVisitor = createApiInstance({
  getState: () => ({
    accessToken: visitorToken,
    refreshToken: null,
    login: () => {},
    logout: () => {},
    user: null
  })
});
